import { PANEL_POSES, POSE_HEADS, POSE_THUMBS } from '../../../lib/story/panelIds';
import type { PanelSpec } from '../../../lib/story/types';
import { logoColor } from '../../../lib/s4/cover';
import type { ArcDef, CoverSpec } from '../../../lib/s4/types';

const INK = '#15121a';
const PAPER = '#e4e3d6';
const CAPTION = '#f3c846';

type Pose = (typeof PANEL_POSES)[number];
const isPose = (p: string | undefined): p is Pose =>
  !!p && (PANEL_POSES as readonly string[]).includes(p);

/**
 * The cover preview (story.html "The cover"): the arc's fixed masthead (logo text in its colour, the issue number, the kicker strip), the
 * panel as stick figures in their poses (a stand-in for the live render, like the cell editor's pose thumbnails, M20), the title burst,
 * the tagline, the "Where" strip and the dog-ear count. Display only.
 */
export function CoverPreview({
  arc,
  number,
  cover,
  panel,
  fallbackTitle,
  where,
  dogEars,
}: {
  arc: ArcDef | undefined;
  number: number;
  cover: CoverSpec;
  /** The panel shown (the default, or a variant's). */
  panel: PanelSpec | null | undefined;
  fallbackTitle: string;
  where: string;
  dogEars: number;
}) {
  const color = logoColor(arc?.logo?.color);
  const logo = (arc?.logo?.text || arc?.title || 'NO ARC').toUpperCase();
  const actors = (panel?.actors ?? []).slice(0, 4);
  const title = (cover.title || fallbackTitle || 'UNTITLED').toUpperCase();
  const step = 200 / (actors.length + 1);
  return (
    <figure className="w-56 shrink-0" data-testid="cover-preview">
      <svg
        viewBox="0 0 200 300"
        role="img"
        aria-label={`Cover preview: ${logo} #${number} ${title}`}
        className="block w-full border-2 border-ink"
      >
        <rect width={200} height={300} fill={PAPER} />
        {/* masthead */}
        <rect width={200} height={42} fill={INK} />
        <text
          x={8}
          y={28}
          fill={color}
          fontFamily="Bangers, Impact, sans-serif"
          fontSize={logo.length > 14 ? 15 : 20}
        >
          {logo}
        </text>
        <rect x={168} y={6} width={26} height={30} fill={PAPER} stroke={color} strokeWidth={2} />
        <text
          x={181}
          y={28}
          textAnchor="middle"
          fill={INK}
          fontFamily="Bangers, Impact, sans-serif"
          fontSize={20}
        >
          {number || '?'}
        </text>
        <rect y={42} width={200} height={14} fill={CAPTION} />
        <text x={100} y={52.5} textAnchor="middle" fill={INK} fontFamily="monospace" fontSize={8}>
          {(cover.kicker || ' ').toUpperCase()}
        </text>
        {/* the panel */}
        <rect x={8} y={62} width={184} height={150} fill="#d6d5c6" stroke={INK} strokeWidth={2} />
        {panel == null ? (
          <text x={100} y={142} textAnchor="middle" fill={INK} fontSize={10}>
            No panel (text-only cover)
          </text>
        ) : actors.length === 0 ? (
          <text x={100} y={142} textAnchor="middle" fill={INK} fontSize={10}>
            {panel.set?.template ? `Set: ${panel.set.template}` : 'Empty panel'}
          </text>
        ) : (
          actors.map((a, i) => {
            const pose: Pose = isPose(a.pose) ? a.pose : 'stand';
            const [hx, hy] = POSE_HEADS[pose];
            return (
              <g key={i} transform={`translate(${step * (i + 1) - 30}, 92) scale(2.5)`}>
                <circle cx={hx} cy={hy} r={3} fill="none" stroke={INK} strokeWidth={1.2} />
                <path
                  d={POSE_THUMBS[pose]}
                  fill="none"
                  stroke={INK}
                  strokeWidth={1.2}
                  strokeLinecap="round"
                />
                <text x={12} y={36} textAnchor="middle" fontSize={3.6} fill={INK}>
                  {a.role || '?'}
                </text>
              </g>
            );
          })
        )}
        {/* title burst */}
        <polygon
          points="20,206 40,196 60,204 90,192 120,203 150,194 182,206 172,222 186,236 150,238 120,248 90,238 60,246 34,236 14,226"
          fill={CAPTION}
          stroke={INK}
          strokeWidth={2}
        />
        <text
          x={100}
          y={226}
          textAnchor="middle"
          fill={INK}
          fontFamily="Bangers, Impact, sans-serif"
          fontSize={title.length > 14 ? 13 : 18}
        >
          {title}
        </text>
        <text x={100} y={262} textAnchor="middle" fill={INK} fontSize={8}>
          {cover.tagline || ''}
        </text>
        {/* where strip */}
        <rect y={274} width={200} height={26} fill={INK} />
        <text x={8} y={290} fill={PAPER} fontFamily="monospace" fontSize={7.5}>
          WHERE: {where.toUpperCase()}
        </text>
        {/* dog-ears */}
        <text x={192} y={290} textAnchor="end" fill={CAPTION} fontFamily="monospace" fontSize={8}>
          {dogEars > 0 ? `◢×${dogEars}` : ''}
        </text>
      </svg>
      <figcaption className="mt-1 text-xs text-bruise">
        Preview only: the app and homebase render the panel live; the web draws an HTML/SVG card.
      </figcaption>
    </figure>
  );
}
